import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * The page is the drop target (spec 2026-09-09-01 § 3).
 *
 * A zone-scoped drop zone is a target the user has to find; on a conversation
 * page the intent is unambiguous — a file dragged onto this page is meant for
 * this thread — so the listener is on `window` and the whole viewport lights
 * up. `AssetDocuments.tsx` has a hand-rolled zone-scoped version that predates
 * this hook; this is the reusable form, and the console can adopt it.
 *
 * Two details that are easy to get wrong and are the reason this is a hook:
 *
 *  - **a depth counter, not a boolean.** `dragleave` fires every time the
 *    pointer crosses into a child element, so a boolean flickers off over every
 *    nested node. Counting enter/leave pairs is the only stable answer.
 *  - **files only.** A text selection or a link being dragged also raises
 *    `dragover`; those carry no `Files` type and must not raise the overlay.
 *
 * `preventDefault` on `dragover` is what stops the browser from navigating away
 * to the dropped file, which is the default and is a lost message.
 */
export function useGlobalFileDrop(
    onFiles: (files: File[]) => void,
    enabled = true,
): { isDragging: boolean } {
    const [isDragging, setIsDragging] = useState(false)
    const depth = useRef(0)
    // Read through a ref: a caller passing an inline closure (the normal case)
    // would otherwise tear the listeners down and re-add them every render.
    const onFilesRef = useRef(onFiles)
    useEffect(() => {
        onFilesRef.current = onFiles
    }, [onFiles])

    const carriesFiles = useCallback((event: DragEvent): boolean => {
        const types = event.dataTransfer?.types
        if (!types) return false

        return Array.from(types).includes('Files')
    }, [])

    useEffect(() => {
        if (!enabled) {
            setIsDragging(false)
            depth.current = 0

            return
        }

        const onEnter = (event: DragEvent) => {
            if (!carriesFiles(event)) return
            depth.current += 1
            setIsDragging(true)
        }
        const onOver = (event: DragEvent) => {
            if (!carriesFiles(event)) return
            // Without this the browser opens the file and the page is gone.
            event.preventDefault()
            if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy'
        }
        const onLeave = (event: DragEvent) => {
            if (!carriesFiles(event)) return
            depth.current = Math.max(0, depth.current - 1)
            if (depth.current === 0) setIsDragging(false)
        }
        const onDrop = (event: DragEvent) => {
            if (!carriesFiles(event)) return
            event.preventDefault()
            depth.current = 0
            setIsDragging(false)
            const files = Array.from(event.dataTransfer?.files ?? [])
            if (files.length > 0) onFilesRef.current(files)
        }

        window.addEventListener('dragenter', onEnter)
        window.addEventListener('dragover', onOver)
        window.addEventListener('dragleave', onLeave)
        window.addEventListener('drop', onDrop)

        return () => {
            window.removeEventListener('dragenter', onEnter)
            window.removeEventListener('dragover', onOver)
            window.removeEventListener('dragleave', onLeave)
            window.removeEventListener('drop', onDrop)
        }
    }, [carriesFiles, enabled])

    return { isDragging }
}
